import { cn } from "@/lib/utils"
import type { TcmClinicSummary } from "../types"

interface TcmClinicCardsProps {
  clinics: TcmClinicSummary[]
  /** The clinic the cross-clinic view is narrowed to; null = every clinic. */
  selectedId: string | null
  onSelect: (organizationId: string | null) => void
}

/** Pear Admin's view across clinics: what each one has to act on. A card narrows the list to that clinic. */
export function TcmClinicCards({ clinics, selectedId, onSelect }: TcmClinicCardsProps) {
  const total = clinics.reduce(
    (t, c) => ({ act: t.act + c.act_today, overdue: t.overdue + c.overdue, active: t.active + c.active }),
    { act: 0, overdue: 0, active: 0 },
  )
  return (
    <div>
      <h2 className="text-sm font-medium text-muted-foreground mb-2">Clinics</h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Card
          on={selectedId === null}
          onClick={() => onSelect(null)}
          title="All clinics"
          count={total.act}
          tone={total.overdue ? "text-destructive" : total.act ? "text-warning" : "text-muted-foreground"}
          line={`${total.overdue} overdue · ${total.active} open`}
        />
        {clinics.map((c) => {
          const parts = [
            c.overdue && `${c.overdue} overdue`,
            c.in_hospital && `${c.in_hospital} in hospital`,
            c.ready_to_bill && `${c.ready_to_bill} to bill`,
            c.notes_not_in_ehr && `${c.notes_not_in_ehr} not in EHR`,
          ].filter(Boolean)
          return (
            <Card
              key={c.organization_id}
              on={selectedId === c.organization_id}
              onClick={() => onSelect(selectedId === c.organization_id ? null : c.organization_id)}
              title={c.organization_name ?? "Clinic"}
              ehr={c.ehr_name}
              count={c.act_today}
              tone={c.overdue ? "text-destructive" : c.act_today ? "text-warning" : "text-muted-foreground"}
              line={c.active === 0 ? "No open cases" : parts.length ? parts.join(" · ") : `${c.active} open`}
            />
          )
        })}
      </div>
    </div>
  )
}

function Card({ on, onClick, title, ehr, count, tone, line }: {
  on: boolean
  onClick: () => void
  title: string
  ehr?: string | null
  count: number
  tone: string
  line: string
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "rounded-xl border bg-card p-3 text-left transition-colors hover:bg-muted/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        on && "ring-2 ring-primary border-primary",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-medium leading-snug line-clamp-2">{title}</span>
        {ehr && <span className="text-[10px] text-muted-foreground shrink-0">{ehr}</span>}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className={cn("text-xl font-semibold tabular-nums", tone)}>{count}</span>
        <span className="text-xs text-muted-foreground">to act on</span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{line}</p>
    </button>
  )
}
